import { CHUNK_SIZE, FallingBlock, TileType, isApple, isSandTile, isExpandedKiliFruit } from './constants';

function isFallingType(tileType: TileType): boolean {
    return tileType === TileType.DIRT || tileType === TileType.GRASS || tileType === TileType.SKULL || tileType === TileType.STICK || tileType === TileType.LOG || tileType === TileType.FLINT || tileType === TileType.HEART_CONTAINER || tileType === TileType.WOODEN_SHIELD || tileType === TileType.STONE_CLUB || tileType === TileType.IRON_SWORD || tileType === TileType.BOOTS || tileType === TileType.ILO_ANU || tileType === TileType.KIWEN_ANPA || tileType === TileType.KIWEN_SOWELI
        || tileType === TileType.KILI_AKESI || tileType === TileType.KILI_ALE || tileType === TileType.KILI_IKE || tileType === TileType.KILI_JELO || tileType === TileType.KILI_KEN || tileType === TileType.KILI_KIWEN || tileType === TileType.KILI_KULUPU || tileType === TileType.KILI_LAWA || tileType === TileType.KILI_SOWELI
        || isSandTile(tileType)
        || isExpandedKiliFruit(tileType);
}

export interface FallingBlockEngineContext {
    blockUpdateLists: Map<string, FallingBlock[]>;
    getTile(x: number, y: number): TileType;
    setTile(x: number, y: number, tileType: TileType): void;
    getBlockUpdateList(cx: number, cy: number): FallingBlock[];
    addFallingBlock(worldX: number, worldY: number, inertiaDir?: number, justAdded?: boolean): void;
    evaporateUnsupportedBranches(centerX: number, rowYs: number[], justAdded?: boolean): void;
    getChunkKey(cx: number, cy: number): string;
}

/** Move-only extraction of WorldData falling-block methods. */
export function propagateInstabilityUpEngine(ctx: FallingBlockEngineContext, worldX: number, worldY: number): void {
    const tile = ctx.getTile(worldX, worldY);
    if (!isFallingType(tile)) return;
    
    // Check if already in falling list
    const cx = Math.floor(worldX / CHUNK_SIZE);
    const cy = Math.floor(worldY / CHUNK_SIZE);
    const list = ctx.getBlockUpdateList(cx, cy);
    const exists = list.some(b => b.x === worldX && b.y === worldY);
    
    if (!exists) {
        // Add to falling list
        list.push({ x: worldX, y: worldY, movedThisTick: false });
    }
    
    // Continue propagating upward
    propagateInstabilityUpEngine(ctx, worldX, worldY - 1);
}

export function tickBlockUpdatesEngine(ctx: FallingBlockEngineContext, loadedChunks: { cx: number; cy: number }[]): { movedBlocks: FallingBlock[]; tileCorrections: { x: number; y: number; tileType: TileType }[] } {
    const movedBlocks: FallingBlock[] = [];
    // Track tile changes from rolling + cascade corrections (sent to clients).
    // Keyed by "x,y" to deduplicate; stores final tile type at each position.
    const correctionMap = new Map<string, { x: number; y: number; tileType: TileType }>();
    const loadedChunkKeys = new Set(loadedChunks.map(c => ctx.getChunkKey(c.cx, c.cy)));

    // Build a set of ALL currently falling blocks (across all chunks)
    const fallingSet = new Set<string>();
    for (const list of ctx.blockUpdateLists.values()) {
        for (const block of list) {
            fallingSet.add(`${block.x},${block.y}`);
        }
    }

    // Collect falling blocks from loaded chunks, handling justAdded grace period
    const allBlocks: { block: FallingBlock; chunkKey: string }[] = [];

    for (const chunkKey of loadedChunkKeys) {
        const list = ctx.blockUpdateLists.get(chunkKey);
        if (list) {
            for (const block of list) {
                block.movedThisTick = false;
                // Blocks just added this tick get a 1-tick grace period:
                // clear the flag so they'll process next tick, but skip them now
                if (block.justAdded) {
                    block.justAdded = false;
                    continue;
                }
                allBlocks.push({ block, chunkKey });
            }
        }
    }

    if (allBlocks.length === 0) return { movedBlocks, tileCorrections: [] };

    // Sort ALL blocks by Y descending (bottom = highest Y first).
    // This is the single unified pass — falls and rolls interleave by Y level.
    allBlocks.sort((a, b) => b.block.y - a.block.y);

    // Single unified pass: each block tries to fall, roll, or stabilize
    for (const { block, chunkKey } of allBlocks) {
        if (block.movedThisTick) continue;

        const key = `${block.x},${block.y}`;

        // Skip if already removed from fallingSet (e.g., picked up)
        if (!fallingSet.has(key)) continue;

        const tileType = ctx.getTile(block.x, block.y);
        if (!isFallingType(tileType)) {
            // Block was picked up or replaced — remove from tracking
            const list = ctx.blockUpdateLists.get(chunkKey);
            if (list) {
                const idx = list.indexOf(block);
                if (idx !== -1) list.splice(idx, 1);
            }
            fallingSet.delete(key);
            continue;
        }

        const belowY = block.y + 1;
        const oldCx = Math.floor(block.x / CHUNK_SIZE);
        const oldCy = Math.floor(block.y / CHUNK_SIZE);
        const newCy = Math.floor(belowY / CHUNK_SIZE);
        const oldChunkKey = ctx.getChunkKey(oldCx, oldCy);
        const newChunkKey = ctx.getChunkKey(oldCx, newCy);

        const fallingIntoUnloaded = !loadedChunkKeys.has(newChunkKey);

        if (fallingIntoUnloaded) {
            processUnloadedColumnEngine(ctx, block.x, oldCx, newCy, loadedChunkKeys);
        }

        const belowTile = ctx.getTile(block.x, belowY);
        const belowKey = `${block.x},${belowY}`;

        // --- VERTICAL FALL ---
        if (belowTile === TileType.SKY) {
            const oldY = block.y;

            ctx.setTile(block.x, block.y, TileType.SKY);
            ctx.setTile(block.x, belowY, tileType);

            fallingSet.delete(key);
            block.y = belowY;
            block.movedThisTick = true;
            fallingSet.add(`${block.x},${block.y}`);

            // Upward propagation
            const aboveY = oldY - 1;
            const aboveTile = ctx.getTile(block.x, aboveY);
            if (isFallingType(aboveTile)) {
                ctx.addFallingBlock(block.x, aboveY);
            }

            if (loadedChunkKeys.has(oldChunkKey) || loadedChunkKeys.has(newChunkKey)) {
                movedBlocks.push({ x: block.x, y: block.y, movedThisTick: true });
            }

            // Move to new chunk's list if crossed boundary
            if (newChunkKey !== oldChunkKey) {
                const oldList = ctx.blockUpdateLists.get(oldChunkKey);
                if (oldList) {
                    const idx = oldList.indexOf(block);
                    if (idx !== -1) oldList.splice(idx, 1);
                }
                const newList = ctx.getBlockUpdateList(oldCx, newCy);
                if (!newList.includes(block)) {
                    newList.push(block);
                }
            }
            continue;
        }

        // --- LEAVES EVAPORATION ---
        if (belowTile === TileType.LEAVES) {
            let leafEnd = belowY;
            while (ctx.getTile(block.x, leafEnd + 1) === TileType.LEAVES) {
                leafEnd++;
            }

            const belowStackTile = ctx.getTile(block.x, leafEnd + 1);
            const stackIsSupported = belowStackTile !== TileType.SKY;

            if (stackIsSupported) {
                // Supported leaves — fall through to stabilization below
            } else {
                // Unsupported leaves — evaporate central column to sticks/apples
                const evaporatedRows: number[] = [];
                for (let ly = belowY; ly <= leafEnd; ly++) {
                    const tileType = isApple(block.x, ly) ? TileType.HEART_CONTAINER : TileType.STICK;
                    ctx.setTile(block.x, ly, tileType);
                    ctx.addFallingBlock(block.x, ly);
                    evaporatedRows.push(ly);
                }
                ctx.evaporateUnsupportedBranches(block.x, evaporatedRows);
                continue;
            }
        }

        // --- BLOCKED: check support stability ---
        if (fallingSet.has(belowKey)) {
            // Resting on another falling block — stay in sync, keep tracking
            continue;
        }

        // --- LOG ROLLING: on stable ground, try diagonal roll ---
        if (tileType === TileType.LOG) {
            let dir = block.inertiaDir;
            if (dir === undefined) {
                dir = (block.y % 2 === 0) ? 1 : -1;
            }

            let rolled = false;
            for (const tryDir of [dir, -dir]) {
                const diagX = block.x + tryDir;
                const diagY = block.y + 1;
                if (ctx.getTile(diagX, block.y) === TileType.SKY &&
                    ctx.getTile(diagX, diagY) === TileType.SKY) {
                    const oldY = block.y;

                    ctx.setTile(block.x, block.y, TileType.SKY);
                    ctx.setTile(diagX, diagY, TileType.LOG);
                    correctionMap.set(`${block.x},${block.y}`, { x: block.x, y: block.y, tileType: TileType.SKY });
                    correctionMap.set(`${diagX},${diagY}`, { x: diagX, y: diagY, tileType: TileType.LOG });

                    // Propagate instability above old position
                    const aboveY = block.y - 1;
                    if (isFallingType(ctx.getTile(block.x, aboveY))) {
                        ctx.addFallingBlock(block.x, aboveY);
                    }

                    const oldKey = `${block.x},${block.y}`;
                    fallingSet.delete(oldKey);

                    block.x = diagX;
                    block.y = diagY;
                    block.inertiaDir = tryDir;
                    block.movedThisTick = true;

                    const newKey = `${block.x},${block.y}`;
                    fallingSet.add(newKey);

                    // Move to new chunk list if crossed boundary
                    const newCx = Math.floor(diagX / CHUNK_SIZE);
                    const rollNewCy = Math.floor(diagY / CHUNK_SIZE);
                    const rollNewChunkKey = ctx.getChunkKey(newCx, rollNewCy);
                    if (rollNewChunkKey !== chunkKey) {
                        const oldList = ctx.blockUpdateLists.get(chunkKey);
                        if (oldList) {
                            const idx = oldList.indexOf(block);
                            if (idx !== -1) oldList.splice(idx, 1);
                        }
                        const newList = ctx.getBlockUpdateList(newCx, rollNewCy);
                        if (!newList.includes(block)) {
                            newList.push(block);
                        }
                    }

                    movedBlocks.push({ x: block.x, y: block.y, movedThisTick: true });

                    rolled = true;
                    break;
                }
            }
            if (rolled) continue;
        }

        // --- STABILIZE: resting on stable ground ---
        const list = ctx.blockUpdateLists.get(chunkKey);
        if (list) {
            const idx = list.indexOf(block);
            if (idx !== -1) list.splice(idx, 1);
        }
        fallingSet.delete(key);
    }

    return { movedBlocks, tileCorrections: Array.from(correctionMap.values()) };
}

// Process a single column in an unloaded chunk (bottom-up, one tile each)
export function processUnloadedColumnEngine(
ctx: FallingBlockEngineContext,
x: number,
cx: number,
cy: number,
loadedChunkKeys: Set<string>
): void {
    const chunkKey = ctx.getChunkKey(cx, cy);
    const list = ctx.blockUpdateLists.get(chunkKey);
    if (!list) return;
    
    // Get blocks in this column only, sorted by Y descending (bottom first)
    const columnBlocks = list.filter(b => b.x === x).sort((a, b) => b.y - a.y);
    if (columnBlocks.length === 0) return;
    
    // Reset stale movedThisTick flags (these blocks weren't reset in main tick)
    for (const block of columnBlocks) {
        block.movedThisTick = false;
    }
    
    // Process each block in column
    for (const block of columnBlocks) {
        if (block.movedThisTick) continue; // Skip if already moved this tick
        
        const tileType = ctx.getTile(block.x, block.y);
        if (!isFallingType(tileType)) continue;
        
        const belowY = block.y + 1;
        const belowCy = Math.floor(belowY / CHUNK_SIZE);
        const belowChunkKey = ctx.getChunkKey(cx, belowCy);
        
        // If falling into another unloaded chunk, process that column first
        if (belowCy !== cy && !loadedChunkKeys.has(belowChunkKey)) {
            processUnloadedColumnEngine(ctx, x, cx, belowCy, loadedChunkKeys);
        }
        
        const belowTile = ctx.getTile(block.x, belowY);
        if (belowTile === TileType.SKY) {
            // Remember old position for upward propagation
            const oldY = block.y;
            
            // Execute fall - preserve original tile type
            ctx.setTile(block.x, block.y, TileType.SKY);
            ctx.setTile(block.x, belowY, tileType);
            block.y = belowY;
            block.movedThisTick = true;
            
            // UPWARD PROPAGATION: check if there's a falling type above old position
            const aboveY = oldY - 1;
            const aboveTile = ctx.getTile(block.x, aboveY);
            if (isFallingType(aboveTile)) {
                ctx.addFallingBlock(block.x, aboveY);
            }
            
            // Move to new chunk list if crossed boundary
            if (belowCy !== cy) {
                const idx = list.indexOf(block);
                if (idx !== -1) list.splice(idx, 1);
                const newList = ctx.getBlockUpdateList(cx, belowCy);
                if (!newList.includes(block)) {
                    newList.push(block);
                }
            }
        }
    }
}
